/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Button } from "semantic-ui-react";
import { selectEditMode } from "../../slices/editMode/editModeSlice";
import { resourceUpdated, selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { changeTopNavigationElements } from "../../slices/navigationPanel/navigationPanelSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { changeComparativeEvidenceMap, selectComparativeEvidenceMap } from "../../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice";
import { loadTopSections, selectAllTopSections, selectTopSectionsStatus, topSectionAdded } from "../../slices/topSections/topSectionsSlice";
import { updateNarrativeElement } from "../../functions/updateElement";
import jsonEquals from "../../functions/jsonEquals";
import generateNarrativeFromSummaryOfFindingsTable from "../compositionResourceFunctions/generateNarrativeFromSummaryOfFindingsTable";
import getNetEffectPointEstimate from "../compositionResourceFunctions/getNetEffectPointEstimate";
import getNetEffectEstimate from "../compositionResourceFunctions/getNetEffectEstimate";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../../datatypeComponents/NarrativeEntry";
import { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import TopSectionSliceDisplay from "./TopSectionSliceDisplay";
import { ReorganizeSectionsModal } from "../ReorderCompositionSections";
import SummaryOfFindingsTable from "./SummaryOfFindingsTable";
import createCompositionTableDict from "../compositionResourceFunctions/createCompositionTableDict";
import Spinner from "../../components/Spinner";

const SummaryOfFindingsDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const resourceCollection = useSelector(state => selectAllResources(state));
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    const comparativeEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const allTopSections = useSelector(selectAllTopSections);
    const topSectionsStatus = useSelector(selectTopSectionsStatus);

    const [compositionResourceState, setCompositionResourceState] = useState({});
    const [generatedNarrativeState, setGeneratedNarrativeState] = useState("");
    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const [sectionNavigationElementIds, setSectionNavigationElementIds] = useState([]);
    const [sectionsReorganizedState, setSectionsReorganizedState] = useState({});
    const [addSectionState, setAddSectionState] = useState({
        "sectionTitle": "", "addSectionClicked": false
    });
    const [findingsSectionIndexState, setFindingsSectionIndexState] = useState(null);
    const [columnHeadersSectionIndexState, setColumnHeadersSectionIndexState] = useState(null);
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    let netEffectPointEstimate = getNetEffectPointEstimate(compositionResourceState) || null;
    let netEffectEstimate = getNetEffectEstimate(compositionResourceState) || null;

    useEffect(() => {
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setCompositionResourceState(olderVersionJson);
                let topNavigationElements = ["Summary", "Summary Table"];
                let sectionHeaderNavigationElements = [];
                if (Array.isArray(olderVersionJson.section)) {
                    for (const section of olderVersionJson.section) {
                        let sectionCode = section?.code?.coding?.[0]?.display || section?.code?.text || section?.code?.coding?.[0]?.code;
                        let sectionFocus = section?.focus?.display || section?.focus?.reference;
                        let sectionHeader = section.title || sectionCode || sectionFocus || "Unidentified Section";
                        topNavigationElements.push(sectionHeader);
                        sectionHeaderNavigationElements.push(sectionHeader);
                    }
                }
                dispatch(changeTopNavigationElements(topNavigationElements));
                const sectionNavigationIds = sectionHeaderNavigationElements.map(((element) => {
                    let id = element.toLowerCase().replace(/\s/g, "-").replace(/\//g, "-");
                    return id;
                }))
                setSectionNavigationElementIds(sectionNavigationIds);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson?.id) {
            setCompositionResourceState(JSON.parse(JSON.stringify(resourceJson)));
            setSectionsReorganizedState({ "reorganized": false, "section": resourceJson.section });
            let topNavigationElements = ["Summary", "Summary Table"];
            let sectionHeaderNavigationElements = [];
            let picoClassifiers = {};
            let populationArray = [];
            let interventionArray = [];
            let comparatorArray = [];
            let outcomeArray = [];
            if (Array.isArray(resourceJson.useContext) && resourceJson.useContext.length > 0) {
                for (const item of resourceJson.useContext) {
                    const code = item?.code.code;
                    if (code === "population" && item.valueCodeableConcept) {
                        populationArray.push({ "concept": item.valueCodeableConcept });
                    }
                    else if (code === "intervention" && item.valueCodeableConcept) {
                        interventionArray.push({ "concept": item.valueCodeableConcept });
                    }
                    else if (code === "comparator" && item.valueCodeableConcept) {
                        comparatorArray.push({ "concept": item.valueCodeableConcept });
                    }
                    else if (code === "outcome-measure" && item.valueCodeableConcept) {
                        outcomeArray.push({ "concept": item.valueCodeableConcept });
                    }
                }
            }
            if (Array.isArray(resourceJson.classification) && resourceJson.classification.length > 0) {
                for (const item of resourceJson.classification) {
                    const code = item?.type?.coding?.[0]?.code;
                    if (code === "population" && item.classifier) {
                        populationArray = item.classifier;
                    }
                    else if (code === "intervention" && item.classifier) {
                        interventionArray = item.classifier;
                    }
                    else if (code === "comparator" && item.classifier) {
                        comparatorArray = item.classifier;
                    }
                    else if (code === "outcome-measure" && item.classifier) {
                        outcomeArray = item.classifier;
                    }
                }
            }
            picoClassifiers = {
                populationArray: populationArray,
                interventionArray: interventionArray,
                comparatorArray: comparatorArray,
                outcomeArray: outcomeArray
            };
            if (Array.isArray(resourceJson.section)) {
                for (const sectionIndex in resourceJson.section) {
                    let section = resourceJson.section[sectionIndex];
                    let sectionCode = section?.code?.coding?.[0]?.display || section?.code?.text || section?.code?.coding?.[0]?.code;
                    let sectionFocus = section?.focus?.display || section?.focus?.reference;
                    let sectionHeader = section.title || sectionCode || sectionFocus || "Unidentified Section";
                    topNavigationElements.push(sectionHeader);
                    sectionHeaderNavigationElements.push(sectionHeader);
                    if (section?.code?.coding?.[0]?.code === "summary-of-findings" &&
                        section.section?.length > 0
                    ) {
                        setFindingsSectionIndexState(sectionIndex);
                    }
                    if (section?.code?.coding?.[0]?.code === "column-headers" &&
                        section.section?.length > 0) {
                        setColumnHeadersSectionIndexState(sectionIndex);
                    }
                }
            }
            dispatch(changeTopNavigationElements(topNavigationElements));
            const sectionNavigationIds = sectionHeaderNavigationElements.map(((element) => {
                let id = element.toLowerCase().replace(/\s/g, "-").replace(/\//g, "-");
                return id;
            }))
            setSectionNavigationElementIds(sectionNavigationIds);
            let combinedDictionary = {};
            for (const entry of resourceCollection) {
                combinedDictionary[entry.id] = entry.revisedResourceJson;
            }
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    combinedDictionary[item.id] = item;
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
            let body = {
                "compositionId": foi,
                "compositionTitle": resourceJson.title || resourceJson.name || parentResourceDetail.displayTitle,
                "researchStudyReference": {},
                "populationReference": {},
                "populationDescriptionGroupReference": {},
                "analyzedGroupReference": {},
                "enrolledGroupReference": {},
                "interventionGroupReference": {},
                "interventionDescriptionReference": {},
                "comparatorGroupReference": {},
                "comparatorDescriptionReference": {},
                "groupAssignmentReference": {},
                "synthesisPopulationReference": {},
                "synthesisInterventionDescriptionReference": {},
                "synthesisComparatorDescriptionReference": {},
                "screenedGroupReference": {},
                "excludedGroupReference": {},
                "groupReferencesDictionary": {},
                "outcomeMeasuresReportReference": {},
                "participantFlowReportReference": {},
                "baselineMeasuresReportReference": {},
                "summaryOfFindingsReference": {},
                "outcomeMeasures": [],
                "baselineMeasures": [],
                "participantFlowMeasures": [],
                "outcomeVariablesListReference": {},
                "participantFlowVariablesListReference": {},
                "baselineVariablesListReference": {},
                "combinedDictionary": combinedDictionary,
                "evidenceDisplayDictionary": {},
                "picoClassifiers": picoClassifiers
            };
            dispatch(changeComparativeEvidenceMap(body));
        }
    }, [resourceJson]);

    useEffect(() => {
        if (topSectionsStatus === "succeeded" && allTopSections?.length > 0) {
            let newCompositionSection = allTopSections.map(entity => { return entity.revisedSection });
            if (newCompositionSection.length > 0) {
                let tableData = createCompositionTableDict({ section: newCompositionSection });
                if (tableData?.Labels) {
                    let generatedNarrative = generateNarrativeFromSummaryOfFindingsTable(tableData,
                        netEffectPointEstimate, netEffectEstimate);
                    setGeneratedNarrativeState(generatedNarrative);
                }
                if (editMode && resourceJson && newCompositionSection !== undefined) {
                    let previousInitialValue = parentResourceDetail.resourceJson.section;
                    if ((previousInitialValue || newCompositionSection.length > 1 || newCompositionSection[0])
                        && !jsonEquals(parentResourceDetail.revisedResourceJson.section, newCompositionSection)
                    ) {
                        console.log("resourceUpdate for Composition.section");
                        let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                        newResourceDetail.revisedResourceJson.section = newCompositionSection;
                        dispatch(resourceUpdated(newResourceDetail));
                    }
                }
            }
        }
    }, [allTopSections]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(compositionResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [compositionResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson && sectionsReorganizedState.reorganized &&
            Array.isArray(sectionsReorganizedState.section) && sectionsReorganizedState.section.length > 0) {
            setSectionsReorganizedState(prevState => { return { ...prevState, "reorganized": false } });
            let oldCompositionSection = allTopSections.map(entity => { return entity.revisedSection });
            if (!jsonEquals(oldCompositionSection, sectionsReorganizedState.section)) {
                let topSectionArray = sectionsReorganizedState.section.map((section, sectionIndex) => {
                    return {
                        id: sectionIndex,
                        displayTitle: section.title ||
                            simpleDisplayStringFromFHIRCodeableConcept(section.code) ||
                            displayStringFromFHIRReference(section.focus) || "Untitled Section",
                        compositionId: foi,
                        productFamily: "SummaryOfFindings",
                        initialSection: section,
                        revisedSection: section
                    };
                });
                let topSectionsBody = { topSections: topSectionArray };
                dispatch(loadTopSections(topSectionsBody));
                console.log("sectionsReorganized so resourceUpdate for Composition.section");
            }
        }
    }, [sectionsReorganizedState]);

    useEffect(() => {
        if (editMode && resourceJson && addSectionState.addSectionClicked &&
            addSectionState.sectionTitle) {
            let sectionLength = allTopSections.length;
            let sectionBody = {
                id: sectionLength,
                displayTitle: addSectionState.sectionTitle,
                compositionId: foi,
                productFamily: "SummaryOfFindings",
                initialSection: { "title": addSectionState.sectionTitle },
                revisedSection: { "title": addSectionState.sectionTitle }
            }
            dispatch(topSectionAdded(sectionBody));
            console.log("addSectionState so resourceUpdate for Composition.section");
            setAddSectionState({
                "sectionTitle": "", "addSectionClicked": false
            });
        }
    }, [addSectionState]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div style={{ marginTop: "12px" }}>
        {(topSectionsStatus === "succeeded" && allTopSections?.length > 0) && <>
            <h3 id="summary">Summary</h3>
            <div style={{ marginLeft: "24px" }}>
                {
                    editMode ?
                        <>
                            <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                generatedNarrative={generatedNarrativeState} startCollapsed
                                startingValue={compositionResourceState.text}
                                calledFrom={"SummaryOfNFindingsDisplay"}
                                setResourceState={setCompositionResourceState} />
                        </>
                        :
                        <>
                            {(compositionResourceState.text?.div && compositionResourceState.text.status && compositionResourceState.text.status !== "empty") ?
                                <DisplayFromFHIRXhtml xhtml={compositionResourceState.text.div} />
                                : <p>[Narrative summary not generated.]</p>}
                        </>
                }
            </div>
            <h3 id="summary-table">Summary Table</h3>

            <SummaryOfFindingsTable
                findingsSectionIndex={findingsSectionIndexState}
                containedResourceDictionary={containedResourceDictionary}
                columnHeadersSectionIndex={columnHeadersSectionIndexState}
                compositionResourceState={compositionResourceState} />
            {editMode && <div>
                <Button className="formButton" style={{ margin: "0px", /*color: "#000000", */ }}
                    content="Reorganize Sections" compact
                    onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                <br />
            </div>}
            {(editSectionModal.openModal) &&
                <>
                    <ReorganizeSectionsModal
                        editSectionModal={editSectionModal}
                        setEditSectionModal={setEditSectionModal}
                        sectionsReorganizedState={sectionsReorganizedState}
                        setSectionsReorganizedState={setSectionsReorganizedState}
                    />
                </>}
            {(compositionResourceState.section?.length > 0) &&
                compositionResourceState.section.map((section, sectionIndex) => {
                    return <div key={sectionIndex}>
                        <TopSectionSliceDisplay foi={foi} containedResourceDictionary={containedResourceDictionary}
                            setCompositionResourceState={setCompositionResourceState}
                            sectionIndex={sectionIndex}
                            resourceReferenceMap={comparativeEvidenceMap}
                            sectionNavigationElementIds={sectionNavigationElementIds} />
                        <br />
                    </div>
                })}
            {editMode &&
                <>
                    <Button style={{ color: "#000000", width: "130px", float: "left" }}
                        className="formButton"
                        content="+ Add Section"
                        onClick={() => {
                            setAddSectionState({
                                "sectionTitle": "Replace with Proper Section Title",
                                "addSectionClicked": true
                            });
                        }}
                    />
                </>}
        </>}
    </div>
}

export default SummaryOfFindingsDisplay;